//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import { PrimaryButton } from '@components/atoms/button/Button';
import { HelpIconWithMargin } from '@components/atoms/help-icon/HelpIcon';
import Label from '@components/atoms/label/Label';
import { BodyErrorText, BodyText } from '@components/atoms/text/Text';
import { TextInput } from '@components/atoms/text-input/TextInput';
import { KEYS } from '@constants/keyboard';
import { isDefined } from '@utils/filterPredicates';
import { keyEquals } from '@utils/keyboardUtils';
import { pluralize } from '@utils/stringUtils';
import { withTracking } from '@utils/withTracking';

interface Props {
  label: string;
  values: string[];
  onTagsChange: (values: string[]) => void;
  maxTags?: number;
  isDisabled?: boolean;
  nonRemovableTags?: string[];
  allowDuplicates?: boolean;
  trackingId?: string;
  validateTag?: (tag: string) => string | true;
  labelHelpText?: string;
  selectedItemsLabel?: string;
}

type TagsInputProps = Props & {
  errorText?: string | [string, string];
  hasError?: boolean;
  instructions?: string;
  placeholder?: string;
};

export const TagsInput = ({
  allowDuplicates = false,
  errorText,
  hasError = false,
  instructions,
  isDisabled = false,
  label,
  labelHelpText,
  maxTags,
  nonRemovableTags = [],
  onTagsChange,
  selectedItemsLabel,
  trackingId,
  validateTag,
  values = [],
  ...inputProps
}: TagsInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [tagError, setTagError] = useState<string | undefined>(undefined);
  const headerLabelTrackingId = isDefined(trackingId)
    ? `${trackingId}-tags-input-header-label`
    : undefined;
  const textInputTrackingId = isDefined(trackingId)
    ? `${trackingId}-tags-input-text-input`
    : undefined;
  const addButtonTrackingId = isDefined(trackingId)
    ? `${trackingId}-tags-input-add-button`
    : undefined;
  const tagLabelTrackingId = isDefined(trackingId)
    ? `${trackingId}-tags-input-tag-label`
    : undefined;

  const isMaxTagsReached = isDefined(maxTags) && values.length >= maxTags;

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleAddTag = () => {
    if (!inputValue.trim()) {
      return;
    }

    // First split by commas, then split each chunk by spaces
    const newTags = inputValue
      .split(',')
      .flatMap((chunk) => chunk.split(' '))
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    // Validate tags if validation function is provided
    if (isDefined(validateTag)) {
      const validationError = newTags
        .map(validateTag)
        .find((result) => result !== true);

      if (isDefined(validationError) && typeof validationError === 'string') {
        setTagError(validationError);
        return;
      }
    }

    // Handle duplicates based on allowDuplicates flag
    const tagsToAdd = allowDuplicates
      ? newTags
      : newTags.filter((tag) => !values.includes(tag));

    // Always clear input after a valid submission attempt
    setTagError(undefined);
    setInputValue('');

    // Only update tags if there are new ones to add
    if (tagsToAdd.length > 0) {
      onTagsChange([...values, ...tagsToAdd]);
    }
  };

  const handleTagRemove = (tagToRemove: string, indexToRemove: number) => {
    if (nonRemovableTags.includes(tagToRemove)) {
      return;
    }

    if (allowDuplicates) {
      onTagsChange([
        ...values.slice(0, indexToRemove),
        ...values.slice(indexToRemove + 1),
      ]);
    } else {
      onTagsChange(values.filter((tag) => tag !== tagToRemove));
    }
  };

  return (
    <Container>
      <HeaderWrapper trackingId={headerLabelTrackingId}>
        <HeaderLabel>{label}</HeaderLabel>
        {!!labelHelpText && <HelpIconWithMargin helpText={labelHelpText} />}
      </HeaderWrapper>
      <InputWrapper>
        <TextInput
          {...inputProps}
          errorText={errorText ?? tagError}
          hasError={hasError || isMaxTagsReached || !!tagError}
          isDisabled={isDisabled || isMaxTagsReached}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (keyEquals(e, KEYS.ENTER)) {
              e.preventDefault();
              handleAddTag();
            }
          }}
          trackingId={textInputTrackingId}
          value={inputValue}
        />
        <StyledPrimaryButton
          isDisabled={isDisabled || !inputValue.trim() || isMaxTagsReached}
          onClick={handleAddTag}
          trackingId={addButtonTrackingId}
        >
          Add
        </StyledPrimaryButton>
      </InputWrapper>
      {values.length > 0 && (
        <SelectedTagsWrapper>
          <BodyText>
            {selectedItemsLabel ?? 'Selected Items'} ({values.length}
            {maxTags ? `/${maxTags}` : ''})
          </BodyText>
          {isDefined(maxTags) && values.length > maxTags && (
            <BodyErrorText>
              Please remove{' '}
              {pluralize(values.length - maxTags, label).toLocaleLowerCase()} to
              meet the maximum limit of {maxTags}
            </BodyErrorText>
          )}
          <TagsListContainer>
            {values.map((tag, index) => (
              <StyledLabel
                key={`${tag}-${index}`}
                decorativeIcon={
                  nonRemovableTags.some(
                    (nonRemovableTag) => nonRemovableTag === tag,
                  )
                    ? 'lock'
                    : undefined
                }
                inline
                isDisabled={
                  isDisabled ||
                  nonRemovableTags.some(
                    (nonRemovableTag) => nonRemovableTag === tag,
                  )
                }
                label={tag}
                onRemove={() => handleTagRemove(tag, index)}
                trackingId={tagLabelTrackingId}
              />
            ))}
          </TagsListContainer>
        </SelectedTagsWrapper>
      )}
    </Container>
  );
};

TagsInput.displayName = 'TagsInput';

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = withTracking(styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacings[1]};
`);

const HeaderLabel = styled(BodyText)`
  margin-bottom: 0; /* to make even with help icon */
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  white-space: nowrap;
`;

const SelectedTagsWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2]};
  margin-top: ${theme.spacings[2]};
`,
);

const TagsListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const InputWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacings[2]};
  align-items: flex-start;
`;

const StyledLabel = withTracking(styled(Label)`
  white-space: nowrap;
`);
